/**
 * Homly — MobileDrawer (Phase 1 refonte UX)
 * =========================================
 * Drawer mobile aligné sur la palette Dark Luxury.
 * Supporte les sections avec sous-menus dépliables.
 */
import React, { useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { HomlyLogo } from '../brand/HomlyLogo';
import CockpitScoreCard from '../dashboard/CockpitScoreCard';

function MobileDrawer({ open, onClose, navItems, basePath, user }) {
    const location = useLocation();
    const initialOpenKey = useMemo(() => {
        const path = location.pathname;
        for (const item of navItems) {
            if (item.children) {
                const hit = item.children.some((c) => path === c.to || path.startsWith(`${c.to}/`));
                if (hit) return item.key;
            }
        }
        return null;
    }, [location.pathname, navItems]);

    const [openKey, setOpenKey] = useState(initialOpenKey);

    if (!open) return null;

    const handleRestartTour = () => {
        onClose();
        setTimeout(() => window.__restartGuidedTour?.(), 200);
    };

    return (
        <div className="md:hidden fixed inset-0 z-50" style={{ background: 'rgba(0, 0, 0, 0.82)', backdropFilter: 'blur(4px)' }} onClick={onClose}>
            <div
                data-testid="mobile-drawer"
                className="absolute left-0 top-0 bottom-0 w-72 animate-slide-in flex flex-col"
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: 'var(--homly-gradient-sidebar)',
                    borderRight: '1px solid var(--homly-border-subtle)',
                    padding: 'var(--homly-space-5)',
                    fontFamily: 'var(--homly-font-body)',
                }}
            >
                <div style={{ marginBottom: 'var(--homly-space-6)' }}>
                    <HomlyLogo size="lg" />
                    <p
                        style={{
                            margin: '6px 0 0 0',
                            fontSize: 10,
                            letterSpacing: '0.16em',
                            textTransform: 'uppercase',
                            color: 'var(--homly-text-tertiary)',
                            fontFamily: 'var(--homly-font-display)',
                            fontWeight: 700,
                        }}
                    >
                        Cockpit IA · {user?.role === 'admin' ? 'Manager' : user?.role === 'agent' ? 'Agent' : 'Vendeur'}
                    </p>
                </div>

                {/* Cockpit Score (admin/agent uniquement) */}
                {(user?.role === 'admin' || user?.role === 'agent') && (
                    <div style={{ marginBottom: 12, marginLeft: -16, marginRight: -16 }}>
                        <CockpitScoreCard basePath={basePath} />
                    </div>
                )}

                <nav className="flex-1 overflow-y-auto" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {navItems.map((item) => {
                        if (item.children) {
                            return (
                                <MobileSection
                                    key={item.key}
                                    section={item}
                                    open={openKey === item.key}
                                    onToggle={() => setOpenKey(openKey === item.key ? null : item.key)}
                                    onLinkClick={onClose}
                                />
                            );
                        }
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.exactMatch || item.to === basePath}
                                onClick={onClose}
                                style={({ isActive }) => ({
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    padding: '12px 14px',
                                    borderRadius: 'var(--homly-radius-md)',
                                    fontSize: 14,
                                    fontFamily: 'var(--homly-font-display)',
                                    fontWeight: 600,
                                    color: isActive ? 'white' : 'var(--homly-text-secondary)',
                                    background: isActive ? 'var(--homly-gradient-purple)' : 'transparent',
                                    textDecoration: 'none',
                                })}
                            >
                                {item.icon && <item.icon size={18} strokeWidth={2.2} />}
                                {item.label}
                            </NavLink>
                        );
                    })}

                    <button
                        onClick={handleRestartTour}
                        data-testid="mobile-drawer-restart-tour"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '12px 14px',
                            borderRadius: 'var(--homly-radius-md)',
                            fontSize: 14,
                            fontFamily: 'var(--homly-font-display)',
                            fontWeight: 600,
                            color: 'var(--homly-text-secondary)',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            marginTop: 8,
                        }}
                    >
                        <HelpCircle size={18} strokeWidth={2.2} />
                        Relancer le tutoriel
                    </button>
                </nav>

                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--homly-border-subtle)' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: 12,
                            background: 'var(--homly-bg-elevated)',
                            border: '1px solid var(--homly-border)',
                            borderRadius: 'var(--homly-radius-md)',
                        }}
                    >
                        <Avatar className="w-10 h-10" style={{ border: '2px solid var(--homly-purple)' }}>
                            <AvatarFallback
                                style={{
                                    background: 'var(--homly-gradient-purple)',
                                    color: 'white',
                                    fontWeight: 700,
                                }}
                            >
                                {user?.first_name?.[0]}
                                {user?.last_name?.[0]}
                            </AvatarFallback>
                        </Avatar>
                        <div style={{ flex: 1 }}>
                            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--homly-text-primary)' }}>
                                {user?.first_name}
                            </p>
                            <p
                                style={{
                                    margin: '2px 0 0 0',
                                    fontSize: 10,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color: 'var(--homly-text-tertiary)',
                                    fontFamily: 'var(--homly-font-mono)',
                                }}
                            >
                                {user?.role}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function MobileSection({ section, open, onToggle, onLinkClick }) {
    const location = useLocation();
    const hasActiveChild = section.children.some(
        (c) => location.pathname === c.to || location.pathname.startsWith(`${c.to}/`)
    );

    return (
        <div>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: '12px 14px',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: 'var(--homly-radius-md)',
                    fontSize: 14,
                    fontFamily: 'var(--homly-font-display)',
                    fontWeight: 600,
                    color: hasActiveChild ? 'var(--homly-text-primary)' : 'var(--homly-text-secondary)',
                    background: hasActiveChild ? 'rgba(168, 85, 247, 0.08)' : 'transparent',
                    textAlign: 'left',
                }}
            >
                {section.icon && (
                    <section.icon
                        size={18}
                        strokeWidth={2.2}
                        style={{ color: hasActiveChild ? 'var(--homly-purple)' : 'inherit' }}
                    />
                )}
                <span style={{ flex: 1 }}>{section.label}</span>
                <ChevronDown
                    size={14}
                    strokeWidth={2.5}
                    style={{
                        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform var(--homly-transition-fast)',
                        opacity: 0.7,
                    }}
                />
            </button>
            {open && (
                <div style={{ marginTop: 4, marginLeft: 14, paddingLeft: 12, borderLeft: '1px solid var(--homly-border-subtle)' }}>
                    {section.children.map((child) => (
                        <NavLink
                            key={child.to}
                            to={child.to}
                            end
                            onClick={onLinkClick}
                            style={({ isActive }) => ({
                                display: 'block',
                                padding: '9px 12px',
                                borderRadius: 'var(--homly-radius-sm)',
                                fontSize: 13,
                                fontFamily: 'var(--homly-font-body)',
                                fontWeight: isActive ? 600 : 500,
                                color: isActive ? 'var(--homly-purple)' : 'var(--homly-text-tertiary)',
                                background: isActive ? 'rgba(168, 85, 247, 0.10)' : 'transparent',
                                textDecoration: 'none',
                            })}
                        >
                            {child.label}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
}

export default MobileDrawer;
